/**
 * Chemistry upload registry: accepted file types, per-key flows, and shared run orchestration
 * (sequential Excel uploads vs ML batch). `ChemUpload.js` calls `createChemUploadMethodMap(...)` once per
 * render (memoized there). Routing lives in `chemUploadResolve.js`; per-key parse/API glue in `chemUploadFlows.js`.
 */

import { toast } from "react-toastify";
import { processExcelResultsMl } from "../../../actions/chemistry";
import { checkReadyReports } from "../../../actions/sendReports";
import {
  moveToHistory,
  moveBatchToHistory,
  uploadChemHistoryExcelFiles,
} from "./Components/History/ChemHistoryHelper";
import { getUser } from "../../../utils/helpers";
import {
  chemUploadProcessAgilentMetals,
  chemUploadProcessDynamicOneToMany,
  chemUploadProcessDynamicOneToOne,
  chemUploadProcessFap,
  chemUploadProcessLegacyChemistrySsann,
  chemUploadProcessLegacyDynamicExcel,
  chemUploadProcessMetals,
  chemUploadProcessMoistureTemplate,
  chemUploadProcessMycotoxin,
  chemUploadProcessPesticides,
  chemUploadProcessPolyphenols,
  chemUploadProcessPunicalagins,
  chemUploadProcessResidualSolvents,
  chemUploadProcessSilverNitrate,
  chemUploadProcessTitratableAcidity,
} from "./ChemUploadMethods";

// const LEGACY_EXCEL_TYPES = [".xls", ".xlsx"];

/**
 * @typedef {"sequential"|"ml"} ChemUploadRegistryKind
 * @typedef {{
 *   acceptedFileTypes: string[],
 *   kind: ChemUploadRegistryKind,
 *   processFile?: (file: File, excelParseContext: ChemUploadExcelParseContext) => Promise<object>,
 *   getHistoryAttachmentPath?: (response: object) => string|null|undefined,
 *   persistHistoryTestName?: string,
 *   persistUploadHistory?: boolean,
 * }} ChemUploadRegistryEntrySpec
 */

/**
 * Selected chemistry test row plus the full tests catalog — passed into each `processFile` so parsers
 * can read column rules from the selection and match dynamic layouts against the list.
 *
 * @typedef {{ selectedTest: object|null, testList: Array }} ChemUploadExcelParseContext
 */

/**
 * @param {File} file
 * @returns {string}
 */
function getChemUploadMlPayloadFileType(file) {
  const fileName = file?.name.toLowerCase();
  const imageExtensions = [".png", ".jpg", ".jpeg", ".gif", ".webp"];
  if (imageExtensions.some((extension) => fileName.endsWith(extension))) {
    return "image";
  }

  const spreadsheetExtensions = [".xls", ".xlsx", ".csv"];
  if (spreadsheetExtensions.some((extension) => fileName.endsWith(extension))) {
    return "xls";
  }

  return "xls";
}

/**
 * Runs one Excel flow per file in order (non-ML tests). Modal rows, toasts, history on success message.
 *
 * @param {{
 *   registryEntry: ChemUploadRegistryEntrySpec,
 *   excelParseContext: ChemUploadExcelParseContext,
 *   setUploadResultsModal: function,
 *   clearSelectedFiles: function,
 * }} params `registryEntry` must be a sequential registry row (`kind: "sequential"`).
 * @param {Array<{ file: File }>} selectedFileEntries
 * @returns {Promise<void>}
 */
export async function runNonMLChemUploadFiles({
  registryEntry,
  excelParseContext,
  setUploadResultsModal,
  clearSelectedFiles,
}, selectedFileEntries) {
  let allUploadsSucceeded = true;
  await selectedFileEntries.reduce(async (previousPromise, selectedFileEntry) => {
    await previousPromise;
    const file = selectedFileEntry.file;
    const response = await registryEntry.processFile(file, excelParseContext);

    if (!response?.success) {
      allUploadsSucceeded = false;
    }

    const nonMlRows = Array.isArray(response.results) ? response.results : [];
    const resultRows = nonMlRows.map((rowItem) => ({ ...rowItem }));
    if (resultRows.length > 0) {
      setUploadResultsModal({
        testName: response.test_name || excelParseContext.selectedTest?.test,
        resultRows,
        isMlUpload: false,
      });
    }

    const canPersistUploadHistory = registryEntry.persistUploadHistory !== false
      && response.success
      && response?.message?.toLowerCase()?.includes("success");
    if (canPersistUploadHistory) {
      checkReadyReports();
      const attachmentPath = typeof registryEntry.getHistoryAttachmentPath === "function"
        ? (registryEntry.getHistoryAttachmentPath(response) ?? null)
        : null;
      await moveToHistory(
        file,
        response,
        registryEntry.persistHistoryTestName ?? excelParseContext.selectedTest?.test,
        attachmentPath,
      );
    }

    if (!response?.success) {
      toast.error(response?.message || "Upload failed");
    }
    if (response?.success) {
      toast.success(response?.message || "Uploaded successfully");
    }
  }, Promise.resolve());

  if (allUploadsSucceeded) {
    clearSelectedFiles();
  }
}

/**
 * ML multi-file batch: S3 upload, `processExcelResultsMl`, modal, batch history.
 *
 * @param {{
 *   excelParseContext: ChemUploadExcelParseContext,
 *   setUploadResultsModal: function,
 *   clearSelectedFiles: function,
 * }} params
 * @param {Array<{ file: File }>} selectedFileEntries
 * @returns {Promise<object>}
 */
export async function runMlChemUploadBatch({
  excelParseContext,
  setUploadResultsModal,
  clearSelectedFiles,
}, selectedFileEntries) {
  const user = getUser();
  const selectedFiles = selectedFileEntries.map((fileEntry) => fileEntry.file);
  const batchUploadResponse = await uploadChemHistoryExcelFiles(selectedFiles);
  const batchUploadInvalid = !batchUploadResponse?.success
    || batchUploadResponse.uploads.length !== selectedFiles.length
    || batchUploadResponse.uploads.some((uploadItem) => !uploadItem?.path);
  if (batchUploadInvalid) {
    throw new Error("Failed to upload file to AWS S3");
  }

  const s3PathList = batchUploadResponse.uploads.map((uploadItem, index) => ({
    s3_excel_path: uploadItem.path,
    file_type: getChemUploadMlPayloadFileType(selectedFiles[index]),
  }));

  const response = await processExcelResultsMl({
    test_name: excelParseContext.selectedTest?.test,
    s3_excel_path_list: s3PathList,
    user_id: user?.id,
    result_type: excelParseContext.selectedTest?.result_type,
  });

  const canUploadMlHistory = response.success
    && response?.message?.toLowerCase()?.includes("success");
  if (canUploadMlHistory) {
    checkReadyReports();
    await moveBatchToHistory(
      selectedFileEntries.map((fileEntry, fileIndex) => {
        const excelPath = s3PathList[fileIndex]?.s3_excel_path;
        const fileNameFromStoragePath = excelPath
          ? excelPath.split("/").filter(Boolean).pop()
          : fileEntry.file.name;
        return {
          fileName: fileNameFromStoragePath,
          excelPath,
        };
      }),
      response,
      excelParseContext.selectedTest?.test,
      null,
    );
  }

  const mlRows = Array.isArray(response.results) ? response.results : [];
  const mlResultRows = mlRows.map((rowItem) => ({ ...rowItem }));
  if (mlResultRows.length > 0) {
    setUploadResultsModal({
      testName: response.test_name || excelParseContext.selectedTest?.test,
      resultRows: mlResultRows,
      isMlUpload: true,
    });
  }
  if (!response?.success) {
    toast.error(response?.message || "Upload failed");
  }
  if (response?.success) {
    toast.success(response?.message || "Uploaded successfully");
    clearSelectedFiles();
  }

  return response;
}

/**
 * Declarative registry: add a row to change routing surface (keys must match `chemUploadResolve`).
 * - `kind: "sequential"` → one file at a time via `runNonMLChemUploadFiles`
 * - `kind: "ml"` → multi-file ML batch
 */

/** @type {Record<string, ChemUploadRegistryEntrySpec>} */
const CHEM_UPLOAD_REGISTRY = {
  fap: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessFap,
    getHistoryAttachmentPath: (uploadResponse) => uploadResponse?.fapPdfPath || null,
  },
  residualSolvents: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessResidualSolvents,
    getHistoryAttachmentPath: (uploadResponse) => uploadResponse?.residualSolventsPdfPath || null,
  },
  metals: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessMetals,
  },
  metalsAgilent: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessAgilentMetals,
  },
  ml: {
    acceptedFileTypes: [".xls", ".xlsx", ".png", ".jpg", ".jpeg"],
    kind: "ml",
  },
  moisture: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessMoistureTemplate,
  },
  polyphenols: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessPolyphenols,
  },
  silverNitrate: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessSilverNitrate,
    persistHistoryTestName: "Salt-AgNO3",
  },
  mycotoxin: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessMycotoxin,
  },
  titratableAcidity: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessTitratableAcidity,
  },
  punicalagins: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessPunicalagins,
  },
  legacyDynamicExcel: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessLegacyDynamicExcel,
  },
  legacyChemistrySsann: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessLegacyChemistrySsann,
  },
  dynamicOneToOne: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessDynamicOneToOne,
  },
  dynamicOneToMany: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessDynamicOneToMany,
  },
  pesticides: {
    acceptedFileTypes: [".xls", ".xlsx"],
    kind: "nonMl",
    processFile: chemUploadProcessPesticides,
    persistUploadHistory: false,
  },
};

/**
 * Builds the per-registry-key upload handlers for the chemistry screen.
 *
 * @param {object} chemUploadScreenProps Values passed from `ChemUpload` (selected test, catalog, modal setters, clear-files).
 * @param {object|null} chemUploadScreenProps.selectedTest Currently selected chemistry test row
 * @param {Array} chemUploadScreenProps.testList Full chemistry tests catalog from the screen
 * @param {function} chemUploadScreenProps.setUploadResultsModal Opens the results modal with row data (`null` closes)
 * @param {function} chemUploadScreenProps.setFiles React state setter; used to clear the file picker after a full success batch
 * @returns {Record<string, { acceptedFileTypes: string[], uploadMethod: function }>}
 */
export function createChemUploadMethodMap(chemUploadScreenProps) {
  const excelParseContext = {
    selectedTest: chemUploadScreenProps.selectedTest,
    testList: chemUploadScreenProps.testList,
  };

  const resultUploadModalControls = {
    setUploadResultsModal: chemUploadScreenProps.setUploadResultsModal,
    clearSelectedFiles: () => chemUploadScreenProps.setFiles([]),
  };

  return Object.fromEntries(
    Object.entries(CHEM_UPLOAD_REGISTRY).map(([registryKey, registryEntry]) => {
      if (registryEntry.kind === "ml") {
        return [
          registryKey,
          {
            acceptedFileTypes: registryEntry.acceptedFileTypes,
            uploadMethod: (selectedFileEntries) => runMlChemUploadBatch(
              { excelParseContext, ...resultUploadModalControls },
              selectedFileEntries,
            ),
          },
        ];
      }

      return [
        registryKey,
        {
          acceptedFileTypes: registryEntry.acceptedFileTypes,
          uploadMethod: (selectedFileEntries) => runNonMLChemUploadFiles(
            {
              ...resultUploadModalControls,
              registryEntry,
              excelParseContext,
            },
            selectedFileEntries,
          ),
        },
      ];
    }),
  );
}
